import { baseClient } from './baseRequest';
import { uploadFileToSignedUrl } from '../utils/uploads';

export type SignedUrlResponse = {
  signedUrl: string;
  fileKey: string;
};

export type KlydoLoopUploadTiming = {
  fileKey?: string;
  signedUrlMs?: number;
  blobMs?: number;
  putMs?: number;
  putBytes?: number;
  processMs?: number;
};

export type UploadFileInput = {
  file: string;
  fileType?: string;
  fileName?: string;
  validatePerfectLoop?: boolean;
  skipModeration?: boolean;
  // Transcode the loop without its audio track (for flows that never play
  // loop sound, e.g. the minimized studio)
  mute?: boolean;
  onUploadProgress?: (progress: number) => void;
  // Reports phase durations of the upload, on success and on failure
  onTiming?: (timing: KlydoLoopUploadTiming) => void;
  options?: {
    crop?: string;
    width?: number;
    height?: number;
    resource_type?: string;
  };
};

// The klydo loop is PUT to a signed URL (not JSON-posted), so it can be sent
// as a raw File/Blob — skipping the base64 encode/decode round trip. The
// base64-string form is still accepted for older call sites.
export type UploadLoopFileInput = Omit<UploadFileInput, 'file'> & {
  file: string | Blob;
};

export enum UploadRejectionReason {
  NotOffensive = 'notOffensive',
  NotSeamless = 'notSeamless',
  CorruptFile = 'corruptFile',
  Copyright = 'copyright',
}

export type UploadResponseStatus = {
  status: 'rejected' | 'approved' | 'error';
  error?: string;
  rejectionReason?: UploadRejectionReason;
};

export type UploadResponse = UploadResponseStatus & {
  url: string;
  duration?: number;
  checksum?: string;
  isPerfectLoop?: boolean;
};

export type UploadsApiClient = {
  uploadProfileImage: (input: UploadFileInput) => Promise<UploadResponse>;
  uploadKlydoLoop: (input: UploadLoopFileInput) => Promise<UploadResponse>;
  uploadCollectionCover: (input: UploadFileInput) => Promise<UploadResponse>;
  uploadArtistsCover: (input: UploadFileInput) => Promise<UploadResponse>;
  uploadCollectionLogo: (input: UploadFileInput) => Promise<UploadResponse>;
  uploadCollectionLogotype: (input: UploadFileInput) => Promise<UploadResponse>;
  uploadCollectionThumbnailBackground: (
    input: UploadFileInput,
  ) => Promise<UploadResponse>;
  uploadSectionMedia: (input: UploadFileInput) => Promise<UploadResponse>;
  generateSectionMediaSignedUrl: (input: {
    fileName: string;
  }) => Promise<SignedUrlResponse>;
  uploadChime: (input: UploadFileInput) => Promise<UploadResponse>;
  uploadStrike: (input: UploadFileInput) => Promise<UploadResponse>;
  uploadAlarm: (input: UploadFileInput) => Promise<UploadResponse>;
};

const uploadsApiClient: UploadsApiClient = {
  uploadKlydoLoop: async (input) => {
    const timing: KlydoLoopUploadTiming = {};
    try {
      const signedUrlStart = Date.now();
      const signedUrlResponse = await baseClient.post<SignedUrlResponse>({
        url: '/uploads/generate-klydo-loop-signed-url',
        body: {
          fileName: input.fileName,
        },
      });
      timing.signedUrlMs = Date.now() - signedUrlStart;
      timing.fileKey = signedUrlResponse.fileKey;

      const putResult = await uploadFileToSignedUrl(
        signedUrlResponse.signedUrl,
        input.file,
      );
      timing.blobMs = putResult.blobMs;
      timing.putMs = putResult.putMs;
      timing.putBytes = putResult.sizeBytes;

      const processStart = Date.now();
      const response = await baseClient.post<UploadResponse>({
        url: '/uploads/process-klydo-loop',
        body: {
          file: signedUrlResponse.fileKey,
          fileType: input.fileType,
          options: input.options,
          validatePerfectLoop: input.validatePerfectLoop,
          skipModeration: input.skipModeration,
          mute: input.mute,
        },
      });
      timing.processMs = Date.now() - processStart;
      return response;
    } finally {
      input.onTiming?.(timing);
    }
  },
  uploadProfileImage: async (input) => {
    return baseClient.post<UploadResponse>({
      url: '/uploads/upload-profile-image',
      body: {
        file: input.file,
        options: input.options,
      },
    });
  },
  uploadCollectionCover: async (input) => {
    return baseClient.post<UploadResponse>({
      url: '/uploads/upload-collection-cover',
      body: {
        file: input.file,
        options: input.options,
      },
    });
  },
  uploadArtistsCover: async (input) => {
    const signedUrlResponse = await baseClient.post<SignedUrlResponse>({
      url: '/uploads/generate-klydo-loop-signed-url',
      body: {
        fileName: input.fileName,
      },
    });

    await uploadFileToSignedUrl(signedUrlResponse.signedUrl, input.file);
    return baseClient.post<UploadResponse>({
      url: '/uploads/process-artists-cover',
      body: {
        file: signedUrlResponse.fileKey,
        options: input.options,
      },
    });
  },
  uploadCollectionLogo: async (input) => {
    return baseClient.post<UploadResponse>({
      url: '/uploads/upload-collection-logo',
      body: {
        file: input.file,
        options: input.options,
      },
    });
  },
  uploadCollectionLogotype: async (input) => {
    return baseClient.post<UploadResponse>({
      url: '/uploads/upload-collection-logotype',
      body: {
        file: input.file,
        options: input.options,
      },
    });
  },
  uploadCollectionThumbnailBackground: async (input) => {
    return baseClient.post<UploadResponse>({
      url: '/uploads/upload-collection-thumbnail-background',
      body: {
        file: input.file,
        options: input.options,
      },
    });
  },
  uploadSectionMedia: async (input) => {
    return baseClient.post<UploadResponse>({
      url: '/uploads/process-section-media',
      body: {
        file: input.file,
        options: input.options,
      },
    });
  },
  generateSectionMediaSignedUrl: async (input) => {
    return baseClient.post<SignedUrlResponse>({
      url: '/uploads/generate-section-media-signed-url',
      body: { fileName: input.fileName },
    });
  },
  uploadChime: async (input) => {
    return baseClient.post<UploadResponse>({
      url: '/uploads/upload-chime',
      body: {
        file: input.file,
        fileType: input.fileType,
        fileName: input.fileName,
      },
    });
  },
  uploadStrike: async (input) => {
    return baseClient.post<UploadResponse>({
      url: '/uploads/upload-strike',
      body: {
        file: input.file,
        fileType: input.fileType,
        fileName: input.fileName,
      },
    });
  },
  uploadAlarm: async (input) => {
    return baseClient.post<UploadResponse>({
      url: '/uploads/upload-alarm',
      body: {
        file: input.file,
        fileType: input.fileType,
        fileName: input.fileName,
      },
    });
  },
};

export default uploadsApiClient;
